import { Injectable } from '@angular/core';
import { Action, Selector, State, StateContext, Store } from '@ngxs/store';
import { AppService } from '@Core/services/proprietary/app.service';
import {
    CompiledObjectRules,
    DesignerRuleNode,
    EvaluatedObjectRules,
    EvaluatedStateObject,
    FormGroupMapping,
    ObjectRuleSet,
    RuleNode
} from '@Core/services/proprietary/object-rule/object-rule.types';
import { StateHelperService } from '@Core/services/proprietary/state-helper.service';
import { cloneDeep } from 'src/ep-utils';
import { UserDefinedValidation } from './page-object.types';
import {
    AddDesignerElementRule,
    AddObjectRuleSets,
    AddPageViews,
    AddSections,
    AddToComponentSettingList,
    AddValidEvent,
    DeleteDesignerElementRule,
    DeleteSection,
    ExpandCollapseChildSections,
    HighlightErrors,
    RefreshObjectRules,
    ReplaceChildSections,
    ResetErrors,
    ResetLock,
    ResetObjectRules,
    ResetPageModel,
    ResetState,
    RestoreValidationRules,
    SaveValidationRules,
    SelectSection,
    SetAllSectionsReadOnly,
    SetAllSectionsVisible,
    SetAnticipatedValidationEvent,
    SetCompiledRules,
    SetCoreSections,
    SetCurrentDesignerComponent,
    SetCurrentDesignerComponentEditMode,
    SetCurrentDesignerElement,
    SetCurrentPageView,
    SetCurrentSection,
    SetCurrentSectionByTitle,
    SetDesignerMode,
    SetDirty,
    SetEvaluatedStateObjects,
    SetLock,
    SetMandatoryEvents,
    SetManualNavigationMode,
    SetMissingEvents,
    SetNavigatorStyle,
    SetObjectValidationEvents,
    SetPageViewSections,
    SetPendingDesignerRuleChange,
    SetPermittedEvents,
    SetPristine,
    SetProtectedMode,
    SetReadOnlyMode,
    SetSectionLayout,
    SetSectionVisible,
    SetSingleSectionMode,
    SetUserDefinedValidations,
    SetValidEvents,
    TrackUnsavedChanges,
    UpdateComponentSetting,
    UpdateDesignerElementRule,
    UpdatePageModel,
    UpdateSectionComponentSettingLink,
    UpdateSectionData,
    UpdateSectionProperties,
    UpdateSectionTitle,
    UpdateSectionUxConfig,
    UpdateSectionUxConfigComponentSettings
} from './page.actions';

//TODO:  create and enumerated type for pageEvent!!!

export enum SectionLayoutType {
    List = 'List',
    Form = 'Form',
    Grid = 'Grid',
    Card = 'Card'
}

export enum SectionNavClassType {
    // values are INTENTIONALLY lowercase.  Used to contstruct section-normal, section-header, etc. css tags
    Normal = 'normal',
    Header1 = 'header1',
    Header2 = 'header2',
    Header3 = 'header3'
}

export enum NavigatorStyleType {
    Left = 'Left',
    None = 'None'
}

export enum EventType {
    Update = 'Update',
    Accept = 'Accept',
    Submit = 'Submit',
    Reject = 'Reject',
    Delete = 'Delete',
    Approve = 'Approve',
    Search = 'Search',
    Rate = 'Rate',
    Void = 'Void',
    Send = 'Send', //Send and Distribute
    Create = 'Create',
    File = 'File',
    Print = 'Print',
    Post = 'Post',
    Skip = 'Skip',
    Complete = 'Complete',
    Start = 'Start',
    Reconcile = 'Reconcile',
    PreClose = 'PreClose',
    Option1 = 'Option1',
    Option2 = 'Option2',
    Option3 = 'Option3',
    Option4 = 'Option4',
    Option5 = 'Option5',
    Option6 = 'Option6',
    Option7 = 'Option7'
}

export enum DesignerComponentType {
    EditPanel = 'EditPanel',
    Collection = 'Collection',
    SectionNavigator = 'SectionNavigator'
}

export enum DesignerElementType {
    Field = 'Field',
    FieldBlock = 'FieldBlock',
    FieldSection = 'FieldSection',
    FieldGroup = 'FieldGroup'
}

export enum DesignerElementPropertyType {
    label = 'label',
    columnSpan = 'columnSpan'
}

export interface ComponentSetting {
    Setting_ID?: string;
    Setting_GUID?: string;
    Parent_Object_Code?: string;
    Edit_Status?: string;
    Component_Name: string;
    Setting_Desc: string;
    Setting_Syntax?: any;
    Setting_Use?: {
        Count: Number;
        PageLayout: [];
    };
}

export interface ComponentSettingSyntax {
    Setting_ID: string; //do we need this?   We may be able to refactor to drop this element
    ChildLayout?: any; //used by edit panel components
    RuleSet?: any; // used by edit panel components
    Config?: any; // used by collection components
}

export interface ComponentSettingLink {
    Setting_ID?: string;
    Edit_Status?: string;
    Component_Name: string;
}

export interface PageStateModel {
    pageEvent: string;
    readOnlyMode: boolean;
    protectedMode: boolean;
    designerMode: boolean;
    currentDesignerComponent: DesignerComponent;
    excludedFromSectionReadOnly: string[];
    dirty: boolean;
    singleSectionMode: boolean;
    currentSection: PageSection;
    selectingSection: PageSection;
    section: PageSection[];
    coreSection: { [key: string]: PageSection };
    pageView: any;
    currentPageView: PageView;
    validEvents: EventType[];
    navigatorStyle: NavigatorStyleType;
    manualNavigationMode: boolean;
    objectRuleSets: { ruleSet: ObjectRuleSet; compiled: boolean }[];
    objectRuleCompileDirty: boolean;
    compiledObjectRules: CompiledObjectRules;
    evaluatedStateObjects: EvaluatedStateObject[];
    evaluatedStateEvent: EventType;
    objectValidationEvents: EventType[];
    // event to validate against for a manuual save/update.   
    // Should be the current state of object (e.g.  If submitted, then this should be submit)
    saveValidationEvent: EventType;
    // event to use for inplace UI validation.  This is the logical next event.   
    // Submit if currently created/unsaved,  Accept if Submitted, etc.   Take into account rights and WF rules.
    anticipatedValidationEvent: EventType;
    permittedEvents: EventType[]; //events that the user currently has rights to perform
    mandatoryEvents: EventType[]; //events that the workflow defines as mandatory to complete a task.
    missingEvents: string[];
    // !!!!! cache edit panel level rules in a dictionary here for performance?????  edit-panel-name: objectRuleSet[]
    savedRules: any;
    pageModel: any;
    trackUnsavedChanges: boolean;
    lock: any;
    componentSettingList: ComponentSetting[];
    userDefinedValidations: UserDefinedValidation[];
    pendingDesignerRuleChange: boolean;
    //componentSetting: { [key: string]: ComponentSetting };
}

export interface PageSection {
    Section_ID?: number;
    Title: string;
    Visible?: boolean;
    ReadOnly?: boolean;
    Display_Order?: number;
    Always_Visible?: boolean;
    Component_Type: string;
    Default_Edit_Layout?: SectionLayoutType;
    Default_ReadOnly_Layout?: SectionLayoutType;
    Current_Layout?: SectionLayoutType;
    Layouts?: SectionLayoutType[];
    Navigator_Component_Type?: string;
    Navigator_Class?: SectionNavClassType;
    Indent_Children?: boolean;
    Child_Sections?: PageSection[];
    Data?: any;
    Parent_Section_ID?: number; //do not set this property
    Child_Section_IDs?: number[]; //do not set this property
    Section_Key?: string;
    Section_Group?: string;
    Help_ID?: string;
    Ux_Config?: any;
    ChildrenCollapsed?: boolean;
    Collapsed?: boolean;
    Edit_Status?: string;
    Section_GUID?: string; // do not set this property
    Core_Section_GUID: string;
    DB_Section_ID?: number;
    ComponentSettings?: any; //strongly type???  case????
    Article_Type?: string;
}

export interface PageView {
    Layout_Object_Code: string;
    View_ID: number;
    Title: string;
    Sections: PageSection[];
    AvailableSections?: PageSection[];
    RuleSets?: ObjectRuleSet[];
    DynamicSections?: PageSection[];
    ComponentSettings?: ComponentSetting[];
}

export interface DesignerComponent {
    name: string;
    type: DesignerComponentType;
    formGroupMapping?: FormGroupMapping[];
    currentElement?: DesignerElement;
    baseChildLayout?: any;
    savedConfig?: any;
    editMode?: boolean;
}

export interface DesignerElement {
    name: string;
    type: DesignerElementType;
    label?: string;
    columnSpan?: string;
    formGroup?: string;
    fieldPath?: string; //do not include the formgroup as part of the field path  Veh_Num not Article.Veh_Num
    format?: string;
    objectNodeType?: string;
    evaluatedFormRules?: EvaluatedObjectRules;
}

export interface RecursiveAddOptions {
    overwrite: boolean;
    singleSectionMode: boolean;
    readOnlyMode: boolean;
    displayIndex: number;
}

//TODO :  enum for lastPageEvent

@State<PageStateModel>({
    name: 'pageState',
    defaults: {
        pageEvent: 'none',
        singleSectionMode: true,
        currentSection: null,
        currentDesignerComponent: null,
        selectingSection: null,
        section: null,
        coreSection: null,
        readOnlyMode: false,
        protectedMode: false,
        designerMode: false,
        excludedFromSectionReadOnly: null,
        dirty: false,
        pageView: null,
        currentPageView: null,
        validEvents: [],
        navigatorStyle: NavigatorStyleType.Left,
        manualNavigationMode: false,
        objectRuleSets: [],
        objectRuleCompileDirty: true,
        compiledObjectRules: {},
        evaluatedStateObjects: [],
        evaluatedStateEvent: null,
        objectValidationEvents: [],
        saveValidationEvent: null,
        anticipatedValidationEvent: null,
        permittedEvents: [],
        mandatoryEvents: [],
        missingEvents: [],
        savedRules: null,
        pageModel: {},
        trackUnsavedChanges: true,
        lock: {},
        componentSettingList: [],
        userDefinedValidations: [],
        pendingDesignerRuleChange: false
        //componentSetting: {}
    }
})
@Injectable()
export class PageState {
    constructor(public helper: StateHelperService, public store: Store, public app: AppService) { }

    @Selector()
    static unsavedChanges(state: PageStateModel) {
        return state.dirty && state.trackUnsavedChanges;
    }

    @Selector()
    static dirty(state: PageStateModel) {
        return state.dirty;
    }

    @Selector()
    static pristine(state: PageStateModel) {
        return !state.dirty;
    }

    @Selector()
    static pageEvent(state: PageStateModel) {
        return state.pageEvent;
    }

    @Selector()
    static singleSectionMode(state: PageStateModel) {
        return state.singleSectionMode;
    }

    @Selector()
    static singleSectionModeAsYN(state: PageStateModel) {
        let result: string = state.singleSectionMode ? 'Y' : 'N';
        return result;
    }

    @Selector()
    static designerMode(state: PageStateModel) {
        return state.designerMode;
    }

    @Selector()
    static currentDesignerComponent(state: PageStateModel) {
        return state.currentDesignerComponent;
    }

    @Selector()
    static currentDesignerComponentSettingList(state: PageStateModel) {
        return state.componentSettingList.filter((x) => x.Component_Name == state.currentDesignerComponent.name);
    }

    @Selector()
    static currentDesignerComponentSettingSyntax(state: PageStateModel) {
        let componentName = state.currentDesignerComponent.name;
        let componentSettingSyntax: ComponentSettingSyntax = state.currentSection.Ux_Config?.[componentName] || {};
        return componentSettingSyntax;
    }

    @Selector()
    static pendingDesignerRuleChange(state: PageStateModel) {
        return state.pendingDesignerRuleChange;
    }

    @Selector()
    static readOnlyMode(state: PageStateModel) {
        let temp: boolean = false;
        if (state.readOnlyMode == true) {
            temp = true;
        }
        if (state.currentSection && state.currentSection.ReadOnly == true) {
            temp = true;
        }
        return temp;
    }

    @Selector()
    static protectedMode(state: PageStateModel) {
        return state.protectedMode;
    }

    @Selector()
    static readOnlyAndNotProtected(state: PageStateModel) {
        return !state.protectedMode && state.readOnlyMode;
    }


    @Selector()
    static validEvents(state: PageStateModel) {
        return state.validEvents;
    }

    @Selector()
    static validPermittedEvents(state: PageStateModel) {
        // returns the list of valid events that the user currently has rights to (based on user rights and workflow step rules)
        let validEvents = state.validEvents;
        let permittedEvents = state.permittedEvents;
        let validPermittedEvents: EventType[] = [];

        // only add valid events that are in the permitted events list
        validEvents.forEach((x) => {
            if (permittedEvents.includes(x)) {
                validPermittedEvents.push(x);
            }
        });
        // console.log(
        //   `debug: valid events = ${JSON.stringify(validEvents)} | permitted events = ${JSON.stringify(
        //     permittedEvents
        //   )} | valid & permitted events = ${JSON.stringify(validPermittedEvents)}`
        // );
        return validPermittedEvents;
    }

    @Selector()
    static currentSection(state: PageStateModel) {
        //NOTE: **** THIS FUNCTION IS DUPLICATED IN "sections, currentSection, and selectingSection" SELECTORS - MAKE ALL CHANGES TO ALL VERSIONS ****
        function mergeParentData(sectionID: string, level: number = 0, indentLevel: number = 0) {
            let data: any = { Level: level, Indent_Level: indentLevel };
            if (state.section[sectionID].Data) {
                data = { ...state.section[sectionID].Data, ...data };
            }
            if (state.section[sectionID].Parent_Section_ID) {
                let indent: boolean = state.section[state.section[sectionID].Parent_Section_ID].Indent_Children;
                let parentData = mergeParentData(
                    state.section[sectionID].Parent_Section_ID,
                    level + 1,
                    indent ? indentLevel + 1 : indentLevel
                );
                data = { ...parentData, ...data, Indent_Level: parentData.Indent_Level, Level: parentData.Level };
            }
            return data;
        }

        if (state.currentSection) {
            return { ...state.currentSection, Data: mergeParentData(state.currentSection.Section_ID.toString()) };
        } else {
            return null;
        }
    }

    @Selector()
    static currentSectionUxConfigMainComponent(state: PageStateModel) {
        let mainConfig: any = {};
        if (state.currentSection && state.currentSection.Ux_Config && state.currentSection.Ux_Config.MainComponent) {
            mainConfig = state.currentSection.Ux_Config.MainComponent;
        }
        return mainConfig;
    }

    @Selector()
    static selectingSection(state: PageStateModel) {
        //NOTE: **** THIS FUNCTION IS DUPLICATED IN "sections, currentSection, and selectingSection" SELECTORS - MAKE ALL CHANGES TO ALL VERSIONS ****
        function mergeParentData(sectionID: string, level: number = 0, indentLevel: number = 0) {
            let data: any = { Level: level, Indent_Level: indentLevel };
            if (state.section[sectionID].Data) {
                data = { ...state.section[sectionID].Data, ...data };
            }
            if (state.section[sectionID].Parent_Section_ID) {
                let indent: boolean = state.section[state.section[sectionID].Parent_Section_ID].Indent_Children;
                let parentData = mergeParentData(
                    state.section[sectionID].Parent_Section_ID,
                    level + 1,
                    indent ? indentLevel + 1 : indentLevel
                );
                data = { ...parentData, ...data, Indent_Level: parentData.Indent_Level, Level: parentData.Level };
            }
            return data;
        }

        if (state.selectingSection) {
            return { ...state.selectingSection, Data: mergeParentData(state.selectingSection.Section_ID.toString()) };
        } else {
            return null;
        }
    }

    @Selector()
    static navigatorStyle(state: PageStateModel) {
        return state.navigatorStyle;
    }

    @Selector()
    static sections(state: PageStateModel) {
        //NOTE: **** THIS FUNCTION IS DUPLICATED IN "sections, currentSection, and selectingSection" SELECTORS - MAKE ALL CHANGES TO ALL VERSIONS ****
        function mergeParentData(sectionID: string, level: number = 0, indentLevel: number = 0) {
            let data: any = { Level: level, Indent_Level: indentLevel };
            if (state.section[sectionID].Data) {
                data = { ...state.section[sectionID].Data, ...data };
            }
            if (state.section[sectionID].Parent_Section_ID) {
                let indent: boolean = state.section[state.section[sectionID].Parent_Section_ID].Indent_Children;
                let parentData = mergeParentData(
                    state.section[sectionID].Parent_Section_ID,
                    level + 1,
                    indent ? indentLevel + 1 : indentLevel
                );
                data = { ...parentData, ...data, Indent_Level: parentData.Indent_Level, Level: parentData.Level };
            }
            return data;
        }

        let result: PageSection[] = [];

        for (var i in state.section) {
            result.push({ ...state.section[i], Data: mergeParentData(i) });
        }
        return result.sort((a, b) => a.Display_Order - b.Display_Order);
    }

    @Selector()
    static sectionByID(state: PageStateModel) {
        return (sectionID: string) => {
            function mergeParentData(sectionID: string, level: number = 0, indentLevel: number = 0) {
                let data: any = { Level: level, Indent_Level: indentLevel };
                if (state.section[sectionID].Data) {
                    data = { ...state.section[sectionID].Data, ...data };
                }
                if (state.section[sectionID].Parent_Section_ID) {
                    let indent: boolean = state.section[state.section[sectionID].Parent_Section_ID].Indent_Children;
                    let parentData = mergeParentData(
                        state.section[sectionID].Parent_Section_ID,
                        level + 1,
                        indent ? indentLevel + 1 : indentLevel
                    );
                    data = { ...parentData, ...data, Indent_Level: parentData.Indent_Level, Level: parentData.Level };
                }
                return data;
            }

            if (sectionID && state.section && state.section[sectionID]) {
                return { ...state.section[sectionID], Data: mergeParentData(sectionID) };
            }
            return null;
        };
    }

    @Selector()
    static sectionHasChildSections(state: PageStateModel) {
        return (sectionID: string) => {
            if (sectionID && state.section && state.section[sectionID]) {
                const section = state.section[sectionID];
                if (
                    section &&
                    section.Child_Section_IDs &&
                    section.Child_Section_IDs.length > 0 &&
                    !section.ChildrenCollapsed
                ) {
                    return true;
                }
            }
            return false;
        };
    }

    @Selector()
    static currentPageView(state: PageStateModel) {
        return state.currentPageView;
    }

    @Selector()
    static pageViews(state: PageStateModel) {
        let result: PageView[] = [];
        for (var i in state.pageView) {
            result.push(state.pageView[i]);
        }
        if (result.length < 2) {
            return []; //if only one section do not return anything
        }
        return result;
    }

    //Returns page view view list even if only one page view
    @Selector()
    static pageViewsForDesigner(state: PageStateModel) {
        let result: PageView[] = [];
        for (var i in state.pageView) {
            result.push(state.pageView[i]);
        }
        return result;
    }

    @Selector()
    static objectRuleSets(state: PageStateModel) {
        return state.objectRuleSets;
    }

    @Selector()
    static currentDesignerElementRules(state: PageStateModel) {
        let rules: DesignerRuleNode[] = [];
        let currentElement = state.currentDesignerComponent?.currentElement;
        let currentDbSectionID = state.currentSection?.DB_Section_ID;
        if (state.currentDesignerComponent && currentElement && currentElement.objectNodeType) {
            if (
                [
                    DesignerElementType.Field,
                    DesignerElementType.FieldBlock,
                    DesignerElementType.FieldSection,
                    DesignerElementType.FieldGroup
                ].includes(state.currentDesignerComponent.currentElement.type)
            ) {
                state.objectRuleSets.forEach((objectRuleSet) => {
                    if (
                        objectRuleSet.ruleSet &&
                        objectRuleSet.ruleSet.objects &&
                        objectRuleSet.ruleSet.objects[currentElement.objectNodeType]
                    ) {
                        //source prop example  
                        //  Core.Contract.Base   
                        //  Config.CDAAVehicleEditComponent.{DB_Section_ID} 
                        //  Config.Layout.MyCustomView or {GUID}?
                        //  prefix of 'Core' is a core rule from code.    Prefix on 'Config' is a rule from a component on a section
                        let source = objectRuleSet.ruleSet.source;
                        let ruleSetDbSectionID = null;
                        let coreRule = true;
                        if (source.split('.')[0] == 'Config') {
                            coreRule = false;
                        }
                        if (!coreRule) {
                            ruleSetDbSectionID = source.split('.')[2]; //format of user rules is Config.XxxxxxEditPanelComponent.NNNNNN 
                            // where NNNN is the DBSectionID for the section
                        }

                        let object = objectRuleSet.ruleSet.objects[currentElement.objectNodeType];
                        if (
                            object.fields &&
                            object.fields[currentElement.fieldPath] &&
                            object.fields[currentElement.fieldPath].rules
                        ) {
                            object.fields[currentElement.fieldPath].rules.forEach((ruleNode) => {
                                let addRule = true;
                                if (
                                    !coreRule &&
                                    (ruleNode.rule.startsWith('hide') ||
                                        ruleNode.rule.startsWith('disable') ||
                                        ruleNode.rule.startsWith('property'))
                                ) {
                                    if (currentDbSectionID != ruleSetDbSectionID) {
                                        addRule = false; // don't include hide, hideIf, disable, disableIf, 
                                        // property if not a core ruleset and the ruleset is not for the current section
                                    }
                                }
                                if (addRule) {
                                    rules.push({
                                        ...ruleNode,
                                        source: source,
                                        core: coreRule,
                                        otherSectionRule: currentDbSectionID != ruleSetDbSectionID
                                    });
                                }
                            });
                        }
                    }
                });
            }
        }
        return rules;
    }

    @Selector()
    static objectRuleCompileDirty(state: PageStateModel) {
        return state.objectRuleCompileDirty;
    }

    @Selector()
    static evaluatedStateObjects(state: PageStateModel) {
        return state.evaluatedStateObjects;
    }

    @Selector()
    static evaluatedStateEvent(state: PageStateModel) {
        return state.evaluatedStateEvent;
    }

    @Selector()
    static compiledObjectRules(state: PageStateModel) {
        return state.compiledObjectRules;
    }

    @Selector()
    static objectValidationEvents(state: PageStateModel) {
        return state.objectValidationEvents;
    }

    @Selector()
    static saveValidationEvent(state: PageStateModel) {
        return state.saveValidationEvent;
    }

    @Selector()
    static anticipatedValidationEvent(state: PageStateModel) {
        return state.anticipatedValidationEvent;
    }

    @Selector()
    static objectValidationEventsAsList(state: PageStateModel) {
        let list = '';
        state.objectValidationEvents.forEach((event) => {
            list = list + event.toString() + ';';
        });
        return list;
    }

    @Selector()
    static permittedEvents(state: PageStateModel) {
        return state.permittedEvents;
    }

    @Selector()
    static mandatoryEvents(state: PageStateModel) {
        return state.mandatoryEvents;
    }

    @Selector()
    static missingEvents(state: PageStateModel) {
        return state.missingEvents;
    }

    @Selector()
    static pageModel(state: PageStateModel) {
        return state.pageModel;
    }

    @Selector()
    static lock(state: PageStateModel) {
        return state.lock || {};
    }

    @Selector()
    static userDefinedValidations(state: PageStateModel) {
        let result: UserDefinedValidation[] = [];
        for (var i in state.userDefinedValidations) {
            //Exclude 'AOK' records.
            if (['ERR', 'WRN'].includes(state.userDefinedValidations[i].Severity)) {
                result.push(state.userDefinedValidations[i]);
            }
        }

        result.sort((a, b) => {
            if (a.Severity < b.Severity) {
                return -1;
            } else if (a.Severity > b.Severity) {
                return 1;
            } else {
                return 0;
            }
        });

        return result;
    }

    @Selector()
    static userDefinedValidationContainsErrors(state: PageStateModel) {
        let errCount = 0;

        for (var i in state.userDefinedValidations) {
            if (state.userDefinedValidations[i].Severity == 'ERR') {
                errCount++;
            }
        }

        return errCount > 0;
    }

    @Selector()
    static userDefinedValidationContainsWarnings(state: PageStateModel) {
        let wrnCount = 0;

        for (var i in state.userDefinedValidations) {
            if (state.userDefinedValidations[i].Severity == 'WRN') {
                wrnCount++;
            }
        }

        return wrnCount > 0;
    }

    //helper function to find a section
    filterSections(sc: StateContext<PageStateModel>, sectionKeys: any): PageSection[] {
        let state = sc.getState();
        let result: PageSection[] = [];
        let sections: PageSection[] = this.store.selectSnapshot(PageState.sections);

        for (var i = 0; i < sections.length; i++) {
            let match = true;
            for (var key in sectionKeys) {
                if (sectionKeys[key] != null) {
                    switch (key) {
                        case 'Section_ID':
                            if (sections[i].Section_ID != sectionKeys[key]) {
                                match = false;
                            }
                            break;
                        case 'Section_Title':
                            if (sections[i].Title != sectionKeys[key]) {
                                match = false;
                            }
                            break;
                        default:
                            // compare key values to properties in Data
                            if (
                                typeof sections[i].Data[key] == 'undefined' ||
                                sections[i].Data[key] == null ||
                                sections[i].Data[key] != sectionKeys[key]
                            ) {
                                match = false;
                            }
                    }
                }
            }
            if (match) {
                result.push(sections[i]);
            }
        }
        return result;
    }

    @Selector()
    static coreSectionByGUID(state: PageStateModel) {
        return (coreSectionGUID: string) => {
            if (coreSectionGUID && state.coreSection && state.coreSection[coreSectionGUID]) {
                return state.coreSection[coreSectionGUID];
            }
            return null;
        };
    }

    @Action(HighlightErrors)
    HighlightErrors(sc: StateContext<PageStateModel>, action: HighlightErrors) {
        sc.patchState({ pageEvent: 'HighlightErrors' });
    }

    @Action(ResetErrors)
    ResetAll(sc: StateContext<PageStateModel>, action: ResetErrors) {
        sc.patchState({ pageEvent: 'Reset' });
    }

    @Action(SetNavigatorStyle)
    setNavigatorStyle(sc: StateContext<PageStateModel>, action: SetNavigatorStyle) {
        sc.patchState({
            navigatorStyle: action.navigatorStyle
        });
    }

    @Action(SetDesignerMode)
    setDesignerMode(sc: StateContext<PageStateModel>, action: SetDesignerMode) {
        this.app.DesignerMode = action.on;
        sc.patchState({
            designerMode: action.on
        });
    }

    @Action(SetPendingDesignerRuleChange)
    setPendingDesignerRuleChange(sc: StateContext<PageStateModel>, action: SetPendingDesignerRuleChange) {
        if (this.app.DesignerMode) {
            sc.patchState({
                pendingDesignerRuleChange: action.on
            });
        }
    }

    @Action(SetCurrentDesignerComponent)
    setCurrentDesignerComponent(sc: StateContext<PageStateModel>, action: SetCurrentDesignerComponent) {
        let state = sc.getState();
        let savedConfig = null;

        if (
            action.designerComponent &&
            state.currentSection &&
            state.currentSection.Ux_Config &&
            state.currentSection.Ux_Config[action.designerComponent.name]
        ) {
            savedConfig = cloneDeep(state.currentSection.Ux_Config[action.designerComponent.name]);
        }
        sc.patchState({
            currentDesignerComponent: { ...action.designerComponent, editMode: false, savedConfig: savedConfig }
        });
    }

    @Action(SetCurrentDesignerComponentEditMode)
    setCurrentDesignerComponentEditMode(sc: StateContext<PageStateModel>, action: SetCurrentDesignerComponentEditMode) {
        let state = sc.getState();
        if (state.currentDesignerComponent && state.currentDesignerComponent.editMode != action.editMode) {
            let currentDesignerComponent: DesignerComponent = cloneDeep(state.currentDesignerComponent);
            currentDesignerComponent.editMode = action.editMode;
            sc.patchState({
                currentDesignerComponent: currentDesignerComponent
            });
        }
    }

    @Action(SetCurrentDesignerElement)
    setCurrentDesignerElement(sc: StateContext<PageStateModel>, action: SetCurrentDesignerElement) {
        let state = sc.getState();
        if (state.currentDesignerComponent) {
            sc.patchState({
                currentDesignerComponent: { ...state.currentDesignerComponent, currentElement: action.element }
            });
        }
    }

    @Action(AddDesignerElementRule)
    addDesignerElementRule(sc: StateContext<PageStateModel>, action: AddDesignerElementRule) {
        let state = sc.getState();
        let changedSection: PageSection = cloneDeep(state.currentSection);
        let pageView: PageView = cloneDeep(state.currentPageView);
        let componentName = action.designerComponent.name;

        let ruleSource = 'Config.' + componentName + '.' + changedSection.DB_Section_ID; //changedSection.Title.replace(/\s/g, '');

        if (!changedSection.Ux_Config) {
            changedSection.Ux_Config = {};
        }
        if (!changedSection.Ux_Config[componentName]) {
            changedSection.Ux_Config[componentName] = {};
        }
        if (!changedSection.Ux_Config[componentName].RuleSet) {
            // TODO:  What should defaultEvents be???
            changedSection.Ux_Config[componentName].RuleSet = {
                source: ruleSource,
                defaultEvents: 'Create;Submit;Accept',
                objects: {}
            };
        }
        changedSection.Ux_Config[componentName].RuleSet.source = ruleSource;

        if (!changedSection.Edit_Status || changedSection.Edit_Status != 'A') {
            changedSection.Edit_Status = 'C';
        }

        for (var controlPath in this.app.designerSelectedElements) {
            let objectNodeType = this.app.designerSelectedElements[controlPath].objectNodeType;
            let fieldPath = this.app.designerSelectedElements[controlPath].fieldPath;

            //TODO:  HACK!!! stateObject will not necessarily be the formGroup Name.   We need to put a dictionary somewhere
            //       to make nodeTypes to thier stateObject.   OR do we look this up from the base rule set?
            //       OR IS STATE OBJECT EVEN NEEDED IN USER RULES
            if (!changedSection.Ux_Config[componentName].RuleSet.objects[objectNodeType]) {
                changedSection.Ux_Config[componentName].RuleSet.objects[objectNodeType] = {
                    stateObject: null,
                    itemIDField: null,
                    fields: {}
                };
            }
            let objFields = changedSection.Ux_Config[componentName].RuleSet.objects[objectNodeType].fields;

            if (!objFields[fieldPath]) {
                objFields[fieldPath] = {};
            }
            if (!objFields[fieldPath].rules) {
                objFields[fieldPath].rules = [];
            }

            // IF this is a property rule then remove any other rules for that same property
            if (action.ruleNode.rule.startsWith('property.')) {
                let ruleName = action.ruleNode.rule.split('(')[0]; //e.g.  property.label
                objFields[fieldPath].rules = objFields[fieldPath].rules.filter(
                    (ruleNode: RuleNode) => !ruleNode.rule.startsWith(ruleName)
                );
                let resetingProperty = false;
                let test = action.ruleNode.rule.substring(action.ruleNode.rule.indexOf('('));
                if (test == '()' || test == "('')" || test == "('null')") {
                    resetingProperty = true;
                }
                if (!resetingProperty) {
                    //only add rule back if not resetting the property
                    objFields[fieldPath].rules.push(action.ruleNode);
                }
            } else {
                //only add rule if the rule does not already exist
                if (
                    objFields[fieldPath].rules.findIndex((existingRuleNode) => existingRuleNode.rule == action.ruleNode.rule) ===
                    -1
                ) {
                    let currRule = action.ruleNode;
                    currRule.id = this.helper.getNextID(objFields[fieldPath].rules, 'id');
                    objFields[fieldPath].rules.push(currRule);
                }
            }
        }

        //add or update the RuleSet on the pageView
        if (!pageView.RuleSets) {
            pageView.RuleSets = [];
        }

        let found = false;
        for (let i = 0; i < pageView.RuleSets.length; i++) {
            if (pageView.RuleSets[i].source == ruleSource) {
                pageView.RuleSets[i] = changedSection.Ux_Config[componentName].RuleSet;
                found = true;
                break;
            }
        }
        if (!found) {
            pageView.RuleSets.push(changedSection.Ux_Config[componentName].RuleSet);
        }

        //TODO: CHANGE TO USE dispatch UpdateSectionUxConfig?
        sc.patchState({
            pendingDesignerRuleChange: true,
            section: { ...state.section, [changedSection.Section_ID]: changedSection },
            currentPageView: pageView,
            dirty: true
        });

        sc.dispatch(new UpdateComponentSetting(changedSection.Ux_Config[componentName]));
        sc.dispatch(new SetCurrentSection(changedSection.Section_ID));
        sc.dispatch(new AddObjectRuleSets(pageView.RuleSets, true)); //$$$$$true indicates reset current rules
    }

    @Action(UpdateDesignerElementRule)
    updateDesignerElementRule(sc: StateContext<PageStateModel>, action: UpdateDesignerElementRule) {
        let state = sc.getState();
        let changedSection: PageSection = cloneDeep(state.currentSection);
        let pageView: PageView = cloneDeep(state.currentPageView);
        let componentName = action.designerComponent.name;
        let ruleSource = 'Config.' + componentName + '.' + changedSection.DB_Section_ID; //changedSection.Title.replace(/\s/g, '');

        if (!changedSection.Edit_Status || changedSection.Edit_Status != 'A') {
            changedSection.Edit_Status = 'C';
        }

        for (var controlPath in this.app.designerSelectedElements) {
            let objectNodeType = this.app.designerSelectedElements[controlPath].objectNodeType;
            let fieldPath = this.app.designerSelectedElements[controlPath].fieldPath;

            if (!changedSection.Ux_Config[componentName].RuleSet.objects[objectNodeType]) {
                changedSection.Ux_Config[componentName].RuleSet.objects[objectNodeType] = {
                    stateObject: null,
                    itemIDField: null,
                    fields: {}
                };
            }
            let objFields = changedSection.Ux_Config[componentName].RuleSet.objects[objectNodeType].fields;

            if (!objFields[fieldPath]) {
                objFields[fieldPath] = {};
            }
            if (!objFields[fieldPath].rules) {
                objFields[fieldPath].rules = [];
            }

            let index = objFields[fieldPath].rules.findIndex((existingRuleNode) => existingRuleNode.id == action.ruleNode.id);
            if (index != -1) {
                objFields[fieldPath].rules[index] = action.ruleNode;
            }
        }

        //add or update the RuleSet on the pageView
        if (!pageView.RuleSets) {
            pageView.RuleSets = [];
        }

        if (!changedSection.Edit_Status || changedSection.Edit_Status != 'A') {
            changedSection.Edit_Status = 'C';
        }

        if (changedSection.Ux_Config[componentName]) {
            let found = false;
            for (let i = 0; i < pageView.RuleSets.length; i++) {
                if (pageView.RuleSets[i].source == ruleSource) {
                    pageView.RuleSets[i] = changedSection.Ux_Config[componentName].RuleSet;
                    found = true;
                    break;
                }
            }
            if (!found) {
                pageView.RuleSets.push(changedSection.Ux_Config[componentName].RuleSet);
            }
        }

        //TODO: CHANGE TO USE dispatch UpdateSectionUxConfig?
        sc.patchState({
            pendingDesignerRuleChange: true,
            section: { ...state.section, [changedSection.Section_ID]: changedSection },
            currentPageView: pageView,
            dirty: true
        });

        sc.dispatch(new UpdateComponentSetting(changedSection.Ux_Config[componentName]));
        sc.dispatch(new SetCurrentSection(changedSection.Section_ID));
        sc.dispatch(new AddObjectRuleSets(pageView.RuleSets, true)); //$$$$$true indicates reset current rules
    }

    @Action(DeleteDesignerElementRule)
    deleteDesignerElementRule(sc: StateContext<PageStateModel>, action: DeleteDesignerElementRule) {
        let state = sc.getState();
        let changedSection: PageSection = cloneDeep(state.currentSection);
        let pageView: PageView = cloneDeep(state.currentPageView);
        let componentName = action.desingnerComponent.name;
        // TODO:  Replace current section title with GUID for the source property  OR  name of component template
        let ruleSource = 'Config.' + componentName + '.' + changedSection.DB_Section_ID; //changedSection.Title.replace(/\s/g, '');

        for (var controlPath in this.app.designerSelectedElements) {
            let objectNodeType = this.app.designerSelectedElements[controlPath].objectNodeType;
            let fieldPath = this.app.designerSelectedElements[controlPath].fieldPath;

            let elementRules: any[] = [];
            try {
                elementRules = changedSection.Ux_Config[componentName].RuleSet.objects[objectNodeType].fields[fieldPath].rules;
                //filter out rules that match (i.e. remove)
                elementRules = elementRules.filter((existingRuleNode) => existingRuleNode.rule != action.ruleNode.rule);
                changedSection.Ux_Config[componentName].RuleSet.objects[objectNodeType].fields[fieldPath].rules = elementRules;
            } catch (error) {
                //do nothing
            }
        }

        //add or update the RuleSet on the pageView
        if (!pageView.RuleSets) {
            pageView.RuleSets = [];
        }

        if (!changedSection.Edit_Status || changedSection.Edit_Status != 'A') {
            changedSection.Edit_Status = 'C';
        }

        if (changedSection.Ux_Config[componentName]) {
            let found = false;
            for (let i = 0; i < pageView.RuleSets.length; i++) {
                if (pageView.RuleSets[i].source == ruleSource) {
                    pageView.RuleSets[i] = changedSection.Ux_Config[componentName].RuleSet;
                    found = true;
                    break;
                }
            }
            if (!found) {
                pageView.RuleSets.push(changedSection.Ux_Config[componentName].RuleSet);
            }
        }

        //TODO: CHANGE TO USE dispatch UpdateSectionUxConfig?
        sc.patchState({
            pendingDesignerRuleChange: true,
            section: { ...state.section, [changedSection.Section_ID]: changedSection },
            currentPageView: pageView,
            dirty: true
        });

        sc.dispatch(new UpdateComponentSetting(changedSection.Ux_Config[componentName]));
        sc.dispatch(new SetCurrentSection(changedSection.Section_ID));
        sc.dispatch(new AddObjectRuleSets(pageView.RuleSets, true)); //$$$$$true indicates reset current rules
    }

    @Action(SetReadOnlyMode)
    setReadOnlyMode(sc: StateContext<PageStateModel>, action: SetReadOnlyMode) {
        let state = sc.getState();
        let readOnly = action.on;

        if (state.protectedMode) {
            //force readonly if protected
            readOnly = true;
        }

        let pageEvent = readOnly ? 'ReadOnlyMode' : 'EditMode';
        let changed = {};
        let changedCount = 0;
        for (var i in state.section) {
            let desiredLayout = readOnly ? state.section[i].Default_ReadOnly_Layout : state.section[i].Default_Edit_Layout;
            if (desiredLayout && state.section[i].Current_Layout != desiredLayout) {
                changed[i] = { ...state.section[i], Current_Layout: desiredLayout };
                changedCount++;
            }
        }

        if (changedCount > 0) {
            sc.patchState({
                pageEvent: pageEvent,
                section: { ...state.section, ...changed },
                readOnlyMode: readOnly
            });
        } else {
            sc.patchState({
                pageEvent: pageEvent,
                readOnlyMode: readOnly
            });
        }
    }

    @Action(SetProtectedMode)
    SetProtected(sc: StateContext<PageStateModel>, action: SetProtectedMode) {
        let state = sc.getState();
        let turnOnReadonly = action.on && !state.readOnlyMode;
        sc.patchState({
            protectedMode: action.on
        });

        if (turnOnReadonly) {
            sc.dispatch(new SetReadOnlyMode(true));
        }
    }

    @Action(SetSingleSectionMode)
    SetSingleSectionMode(sc: StateContext<PageStateModel>, action: SetSingleSectionMode) {
        let state = sc.getState();
        sc.patchState({ singleSectionMode: action.on });
        if (action.on) {
            sc.dispatch(new SetCurrentSection());
        }
    }

    @Action(SetManualNavigationMode)
    setManualNavigationMode(sc: StateContext<PageStateModel>, action: SetManualNavigationMode) {
        let state = sc.getState();
        sc.patchState({ manualNavigationMode: action.on });
    }

    @Action(SetCurrentSectionByTitle)
    SetCurrentSectionByTitle(sc: StateContext<PageStateModel>, action: SetCurrentSectionByTitle) {
        let state = sc.getState();
        let sectionID = null;
        for (var i in state.section) {
            if (action.title == state.section[i].Title) {
                sectionID = state.section[i].Section_ID;
                break;
            }
        }
        sc.dispatch(new SetCurrentSection(sectionID));
    }

    @Action(SetCurrentSection)
    SetCurrentSection(sc: StateContext<PageStateModel>, action: SetCurrentSection) {
        let state = sc.getState();
        let currentSectionID = -1;
        if (state.currentSection && state.currentSection.Section_ID) {
            currentSectionID = state.currentSection.Section_ID;
        }
        if (action.sectionID === null) {
            for (var i in state.section) {
                action.sectionID = state.section[i].Section_ID;
                break;
            }
        }
        if (state.section[action.sectionID]) {
            sc.patchState({ currentSection: state.section[action.sectionID] });
            //if (state.section[action.sectionID].Visible == false) {
            sc.dispatch(new SetSectionVisible(action.sectionID, true)); //TODO: is this efficient to do here or should we patch state directly?
            //}
            if (state.designerMode) {
                //if in designer mode, clear the current component when navigating to a new section
                if (action.sectionID != currentSectionID) {
                    sc.dispatch(new SetCurrentDesignerComponent(null));
                }
            }
        }
    }

    @Action(SelectSection)
    SelectSection(sc: StateContext<PageStateModel>, action: SelectSection) {
        let state = sc.getState();
        if (action.sectionID === null) {
            for (var i in state.section) {
                action.sectionID = state.section[i].Section_ID;
                break;
            }
        }
        if (state.section[action.sectionID]) {
            sc.patchState({ selectingSection: state.section[action.sectionID] });
            if (state.manualNavigationMode === false) {
                sc.dispatch(new SetCurrentSection(action.sectionID));
            }
        }
    }

    @Action(SetCurrentPageView)
    SetCurrentPageView(sc: StateContext<PageStateModel>, action: SetCurrentPageView) {
        let state = sc.getState();
        let pageView = cloneDeep(state.pageView[action.viewID]);
        let ruleSets = pageView.RuleSets;
        if (!ruleSets) {
            ruleSets = [];
        }

        if (pageView) {
            sc.patchState({
                currentSection: null,
                currentPageView: pageView,
                componentSettingList: []
                // componentSetting: {} ///NEED TO LOAD FROM PAGEVIEW
            });
            sc.dispatch(new AddSections(pageView.Sections, true)); //true indicates reset current sections
            sc.dispatch(new AddObjectRuleSets(ruleSets, true)); //true indicates reset current rules
        }
    }

    @Action(SetPageViewSections)
    setPageViewSections(sc: StateContext<PageStateModel>, action: SetPageViewSections) {
        let state = sc.getState();
        let viewID = "view"; //state.currentPageView.View_ID;

        //let pageView = { ...state.pageView[viewID] };
        //state.currentPageView.Sections = [];
        //state.currentPageView.Sections = action.sections;

        //state.section = action.sections;

        sc.patchState({
            section: [...action.sections]
        });

        // sc.patchState({
        //     pageView: { ...state.pageView, [viewID]: pageView }
        // });
        sc.dispatch(new AddSections(action.sections, true)).subscribe(() => {
            sc.dispatch(new SelectSection());
        });
    }

    @Action(SetCoreSections)
    setCoreSections(sc: StateContext<PageStateModel>, action: SetCoreSections) {
        let state = sc.getState();
        let coreSection = {};

        action.coreSections.forEach((s) => (coreSection[s.Core_Section_GUID] = s));
        sc.patchState({
            coreSection: coreSection
        });
    }

    @Action(SetSectionLayout)
    SetSectionLayout(sc: StateContext<PageStateModel>, action: SetSectionLayout) {
        let state = sc.getState();
        if (state.section[action.sectionID]) {
            sc.patchState({
                section: {
                    ...state.section,
                    [action.sectionID]: { ...state.section[action.sectionID], Current_Layout: action.Layout }
                }
            });
        }
    }

    @Action(SetSectionVisible)
    SetSectionVisible(sc: StateContext<PageStateModel>, action: SetSectionVisible) {
        let id = action.sectionID.toString();
        let state = sc.getState();
        let changed = {
            [action.sectionID]: { ...state.section[action.sectionID], Visible: action.visible }
        };
        let changedCount = 0;

        //set other sections not visible
        if (state.singleSectionMode) {
            for (var i in state.section) {
                if (i != id && state.section[i].Always_Visible != true && state.section[i].Visible == true) {
                    changed[i] = { ...state.section[i], Visible: false };
                    changedCount = changedCount + 1;
                }
            }
        }

        if (action.visible == false && state.singleSectionMode && changedCount == 0) {
            return; //Don't set this section invisible if there are no other visible sections
        }

        sc.patchState({ section: { ...state.section, ...changed } });
    }

    @Action(SetAllSectionsVisible)
    SetAllSectionsVisible(sc: StateContext<PageStateModel>, action: SetAllSectionsVisible) {
        let state = sc.getState();
        let changed = {};
        for (var i in state.section) {
            if (state.section[i].Visible != true) {
                changed[i] = { ...state.section[i], Visible: true };
            }
        }

        sc.patchState({ section: { ...state.section, ...changed } });
    }

    @Action(SetAllSectionsReadOnly)
    SetAllSectionsReadOnly(sc: StateContext<PageStateModel>, action: SetAllSectionsReadOnly) {
        let state = sc.getState();
        let excludeMode = action.excludingSections != null && action.excludingSections.length > 0;
        let changed = {};

        for (var i in state.section) {
            if (excludeMode) {
                //exclude if section id in exclude list
                if (action.excludingSections.includes(i)) {
                    continue;
                }
                //exclude if section title in exclude list (case sensitive)
                if (action.excludingSections.includes(state.section[i].Title)) {
                    continue;
                }
                //exclude if section component type in exclude list (case sensitive)
                if (action.excludingSections.includes(state.section[i].Component_Type)) {
                    continue;
                }
            }
            changed[i] = { ...state.section[i], ReadOnly: action.readOnly };
        }

        sc.patchState({
            section: { ...state.section, ...changed },
            excludedFromSectionReadOnly: action.readOnly ? action.excludingSections : null
        });
    }

    // internal helper function for AddSections
    recursiveAddSections(sections: PageSection[], options: RecursiveAddOptions, parentSectionID: number, changed: any) {
        let sectionIDs = [];
        if (!sections?.length)
            return;

        for (var i = 0; i < sections.length; i++) {
            let newID = parseInt(this.helper.getTempID(), 10) * -1; // Renumbers Section ID's
            changed[newID] = {
                //defaults
                Indent_Children: true,
                Navigator_Class: SectionNavClassType.Normal,
                Always_Visible: false,
                Default_Edit_Layout: SectionLayoutType.Form,
                Default_ReadOnly_Layout: SectionLayoutType.Form,
                Current_Layout: SectionLayoutType.Form,
                Layouts: [SectionLayoutType.Form],
                Navigator_Component_Type: null,
                ReadOnly: false,
                //add values passed in
                ...sections[i],
                //add generated values
                Section_ID: newID,
                Parent_Section_ID: parentSectionID,
                Child_Section_IDs: [],
                Visible: (options.overwrite && i == 0 && parentSectionID === null) || !options.singleSectionMode,
                Display_Order: options.displayIndex++
            };

            if (!changed[newID].DB_Section_ID) {
                changed[newID].DB_Section_ID = this.helper.getTempID();
            }

            changed[newID].Current_Layout = options.readOnlyMode
                ? changed[newID].Default_ReadOnly_Layout
                : changed[newID].Default_Edit_Layout;

            // Load the children of this section
            // TODO: This only does one level -- support recursion to multiple levels?
            sectionIDs.push(newID);
            if (sections[i].Child_Sections && sections[i].Child_Sections.length > 0) {
                this.recursiveAddSections(sections[i].Child_Sections, options, newID, changed);
            }
        }
        if (parentSectionID > 0) {
            changed[parentSectionID].Child_Section_IDs = sectionIDs;
            changed[parentSectionID].Child_Sections = null; //rdon't retain child section object
        }
    }

    @Action(AddSections)
    AddSections(sc: StateContext<PageStateModel>, action: AddSections) {
        let state = sc.getState();
        let changed = {};

        let options: RecursiveAddOptions = {
            displayIndex: 1,
            overwrite: action.overwrite,
            singleSectionMode: state.singleSectionMode,
            readOnlyMode: state.readOnlyMode
        };

        let sections: PageSection[] = cloneDeep(action.sections);

        this.recursiveAddSections(sections, options, null, changed);

        let newState: any = null;

        if (action.overwrite) {
            // overwrite existing sections completely
            newState = { section: { ...changed } };
            // newState.pageView = [];
            // newState.currentPageView = null;
        } else {
            let displayIndex = 1;
            let currentSection: PageSection[] = cloneDeep(state.section);
            let sortedArr: PageSection[] = [];
            let newSection: { [key: string]: PageSection } = {};
            let sectionsAdded = false;
            let sectionFound = false;

            //Get a sorted copy of sections
            for (var k in currentSection) {
                sortedArr.push(currentSection[k]);
            }
            sortedArr = sortedArr.sort((a, b) => a.Display_Order - b.Display_Order);

            if (action.insertAfterSectionID === 0) {
                // Special Case: add new sections to beginning of list
                sectionsAdded = true;
                for (var y in changed) {
                    newSection[y] = changed[y];
                    newSection[y].Display_Order = displayIndex++;
                }
            }

            for (var x = 0; x < sortedArr.length; x++) {
                let id = sortedArr[x].Section_ID;

                if (!sectionsAdded && sectionFound && sortedArr[x].Parent_Section_ID === null) {
                    //insert before next section at root level after the "insertAfterSectionID" section is found.
                    //insert after only works at ROOT level.    If Parent ID provided, new sections alway inserted at first children of parent
                    sectionsAdded = true;
                    for (var y in changed) {
                        newSection[y] = changed[y];
                        newSection[y].Display_Order = displayIndex++;
                    }
                }

                newSection[id] = sortedArr[x];
                newSection[id].Display_Order = displayIndex++;

                if (!sectionFound && action.insertAfterSectionID === id) {
                    // set flag that section is found.
                    // Loop will continue until next root level position is found (Parent_Section_ID is null).
                    // At that point the new sections will be inserted.
                    sectionFound = true;
                }

                if (!sectionsAdded && sectionFound && action.ParentSectionID > 0) {
                    // If parent id passed in, insert the sections immediately following the parent
                    sectionsAdded = true;
                    for (var y in changed) {
                        newSection[y] = changed[y];
                        newSection[y].Display_Order = displayIndex++;
                        if (newSection[y].Parent_Section_ID === null) {
                            // for root sections (parent is null) in the ADD list 
                            // -- add parent id to section and section id to parent's child section array
                            newSection[y].Parent_Section_ID = action.ParentSectionID;
                            newSection[action.ParentSectionID].Child_Section_IDs.push(parseInt(y, 10));
                        }
                    }
                }
            }

            if (!sectionsAdded) {
                // add new sections to end if insertafter not provided (or does not exist)
                for (var y in changed) {
                    newSection[y] = changed[y];
                    newSection[y].Display_Order = displayIndex++;
                }
            }

            newState = { section: newSection };
        }

        // Add properties from core section definition (Layouts, Help_ID)
        for (var z in newState.section) {
            if (newState.section[z].Core_Section_GUID) {
                let coreSectionGUID = newState.section[z].Core_Section_GUID;
                if (state.coreSection && state.coreSection[coreSectionGUID]) {
                    newState.section[z].Layouts = state.coreSection[coreSectionGUID].Layouts;
                    newState.section[z].Help_ID = state.coreSection[coreSectionGUID].Help_ID;
                }
            }
        }

        //sc.patchState({ section: { ...state.section, ...changed } })
        sc.patchState(newState); //now replaces sections rather than adding to existing sections   TODO: append action paramenter not implemented yet

        if (state.designerMode) {
            // If in designermode,  we need to rebuild the rulesets when sections are added/deleted since 
            // section-based rulesets are normally built when during AddPageViews.
            let tempPageView: PageView = cloneDeep(state.currentPageView);
            tempPageView.Sections = [];
            for (var i in changed) {
                tempPageView.Sections.push(changed[i]);
            }
            let ruleSets: ObjectRuleSet[] = this.buildUxConfigRuleSets(tempPageView);
            sc.dispatch(new AddObjectRuleSets(ruleSets, true));
        }

        if (state.excludedFromSectionReadOnly && state.excludedFromSectionReadOnly.length > 0) {
            sc.dispatch(new SetAllSectionsReadOnly(true, state.excludedFromSectionReadOnly));
        }

        if (state.currentSection == null || !state.section[state.currentSection.Section_ID]) {
            sc.dispatch(new SelectSection());
        }
    }

    @Action(ReplaceChildSections)
    replaceChildeSections(sc: StateContext<PageStateModel>, action: ReplaceChildSections) {
        let state = sc.getState();
        let tempSection: PageSection[] = cloneDeep(state.section);
        this.recursiveDeleteSection(tempSection, action.parentSectionID, true); // delete existing children,  "true" param means don't delete parent
        sc.patchState({ section: tempSection });
        sc.dispatch(new AddSections(action.sections, false, action.parentSectionID, action.parentSectionID));
    }

    @Action(ExpandCollapseChildSections)
    expandCollapseChildSections(sc: StateContext<PageStateModel>, action: ExpandCollapseChildSections) {
        let state = sc.getState();
        let section: PageSection[] = cloneDeep(state.section);
        let collapsedValue = !section[action.sectionID].ChildrenCollapsed;
        this.recursiveExpandCollapseChildren(section, action.sectionID, collapsedValue, false);
        sc.patchState({ section: section });
    }

    // internal helper function for toggling the collapsed property on a section's childSections
    recursiveExpandCollapseChildren(
        section: PageSection[],
        sectionID: number,
        collapsedValue: boolean,
        updateSection: boolean = true
    ) {
        if (section[sectionID]) {
            if (section[sectionID].Child_Section_IDs && section[sectionID].Child_Section_IDs.length > 0) {
                for (var x = 0; x < section[sectionID].Child_Section_IDs.length; x++) {
                    let childID: any = section[sectionID].Child_Section_IDs[x];
                    this.recursiveExpandCollapseChildren(section, childID, collapsedValue, true);
                }
                section[sectionID].ChildrenCollapsed = collapsedValue;
            }

            if (updateSection) {
                section[sectionID].Collapsed = collapsedValue;
            }
        }
    }

    @Action(DeleteSection)
    deleteSection(sc: StateContext<PageStateModel>, action: DeleteSection) {
        let state = sc.getState();
        let tempSection: PageSection[] = cloneDeep(state.section);
        this.recursiveDeleteSection(tempSection, action.SectionID);
        sc.patchState({ section: tempSection });
        sc.dispatch(new SelectSection());
    }

    // internal helper function for deleteSection and replaceChildSections
    recursiveDeleteSection(section: PageSection[], sectionID: number, keepParent: boolean = false) {
        if (section[sectionID]) {
            if (section[sectionID].Child_Section_IDs) {
                // delete child sections
                for (var x = 0; x < section[sectionID].Child_Section_IDs.length; x++) {
                    let childID: any = section[sectionID].Child_Section_IDs[x];
                    this.recursiveDeleteSection(section, childID);
                }
            }
            section[sectionID].Child_Section_IDs = [];
            if (!keepParent) {
                delete section[sectionID];
            }
        }
    }

    @Action(UpdateSectionData)
    updateSectionData(sc: StateContext<PageStateModel>, action: UpdateSectionData) {
        // updates all sections that match all sectionKeys
        let state = sc.getState();
        let changed: any = {};
        let sections: PageSection[] = this.filterSections(sc, action.sectionKey);

        for (var i = 0; i < sections.length; i++) {
            let id = sections[i].Section_ID;
            let data = { ...sections[i].Data, ...action.data };
            changed[id] = { ...sections[i], Data: data };
        }

        sc.patchState({ section: { ...state.section, ...changed } });
    }

    @Action(UpdateSectionTitle)
    updateSectionTitle(sc: StateContext<PageStateModel>, action: UpdateSectionTitle) {
        let state = sc.getState();
        let sectionID = action.sectionID || state.currentSection.Section_ID;

        if (state.section[sectionID]) {
            let changed: any = { ...state.section[sectionID] };
            changed.Title = action.title || '';
            sc.patchState({ section: { ...state.section, [sectionID]: changed } });
        }
    }

    @Action(UpdateSectionUxConfig)
    updateSectionUxConfig(sc: StateContext<PageStateModel>, action: UpdateSectionUxConfig) {
        let state = sc.getState();
        let sectionID = action.sectionID || state.currentSection.Section_ID;
        if (state.section[sectionID]) {
            let changed: any = { ...state.section[sectionID] };
            let currentUxConfig = cloneDeep(changed.Ux_Config);
            let newUxConfig = cloneDeep(action.uxConfig);
            changed.Ux_Config = { ...currentUxConfig, ...newUxConfig };

            // loop through components and then thru component settings to remove (delete) and empty settings or components.
            for (var component in changed.Ux_Config) {
                for (var settingNode in changed.Ux_Config[component]) {
                    if (changed.Ux_Config[component][settingNode] == null) {
                        delete changed.Ux_Config[component][settingNode];
                    }
                }
                if (Object.keys(changed.Ux_Config[component]).length == 0) {
                    delete changed.Ux_Config[component];
                }
            }
            // if no components then set the ux config property to null
            if (Object.keys(changed.Ux_Config).length == 0) {
                changed.Ux_Config = null;
            }

            if (!changed.Edit_Status || changed.Edit_Status != 'A') {
                changed.Edit_Status = 'C';
            }

            sc.patchState({
                section: { ...state.section, [sectionID]: changed },
                dirty: true
                // componentSetting: componentSettingState
            });

            for (var component in newUxConfig) {
                sc.dispatch(new UpdateComponentSetting(newUxConfig[component]));
            }

            if (action.refeshRuleSets) {
                sc.dispatch(new RefreshObjectRules());
            } else {
                sc.dispatch(new SetPendingDesignerRuleChange(false));
            }
            sc.dispatch(new SetCurrentSection(sectionID));
        }
    }

    @Action(UpdateSectionUxConfigComponentSettings)
    updateSectionUxConfigComponentSettings(
        sc: StateContext<PageStateModel>,
        action: UpdateSectionUxConfigComponentSettings
    ) {
        let state = sc.getState();
        let sectionID = action.sectionID || state.currentSection.Section_ID;
        let refreshRuleSets = action.settingNode == 'RuleSet';
        if (state.section[sectionID]) {
            let section: any = state.section[sectionID];
            let newConfig: any = {};
            if (section.Ux_Config && section.Ux_Config[action.componentName]) {
                newConfig = cloneDeep(section.Ux_Config[action.componentName]);
            }

            newConfig[action.settingNode] = action.settings;

            sc.dispatch(new UpdateSectionUxConfig(sectionID, { [action.componentName]: newConfig }, refreshRuleSets));
        }
    }

    @Action(UpdateSectionComponentSettingLink)
    updateSectionComponentSettingLink(sc: StateContext<PageStateModel>, action: UpdateSectionComponentSettingLink) {
        let state = sc.getState();
        let sectionID = action.sectionID || state.currentSection.Section_ID;
        let settingID = action.componentSettingLink.Setting_ID;
        let componentName = action.componentSettingLink.Component_Name;

        if (state.section[sectionID]) {
            let section: PageSection = cloneDeep(state.section[sectionID]);
            let sectionComponentSettingLinks: ComponentSettingLink[] = [];

            if (section.ComponentSettings) {
                sectionComponentSettingLinks = cloneDeep(section.ComponentSettings);
            }

            let existingIndex = sectionComponentSettingLinks.findIndex((x) => x.Component_Name == componentName);
            if (existingIndex < 0) {
                if (action.componentSettingLink.Setting_ID != null && action.componentSettingLink.Setting_ID != 'null') {
                    sectionComponentSettingLinks.push({ ...action.componentSettingLink, Edit_Status: 'A' });
                }
            } else {
                if (action.componentSettingLink.Setting_ID == null || action.componentSettingLink.Setting_ID == 'null') {
                    if (sectionComponentSettingLinks[existingIndex].Edit_Status == 'A') {
                        sectionComponentSettingLinks.splice(existingIndex, 1); //removed item from array
                    } else {
                        sectionComponentSettingLinks[existingIndex].Edit_Status = 'D';
                    }
                } else {
                    sectionComponentSettingLinks[existingIndex].Setting_ID = action.componentSettingLink.Setting_ID;
                    if (sectionComponentSettingLinks[existingIndex].Edit_Status != 'A') {
                        sectionComponentSettingLinks[existingIndex].Edit_Status = 'C';
                    }
                }
            }

            section.ComponentSettings = sectionComponentSettingLinks;

            //update the Ux_Config to have settings for this component
            if (!section.Ux_Config) {
                section.Ux_Config = {};
            }
            if (action.componentSetting) {
                let settingSyntax = cloneDeep(action.componentSetting.Setting_Syntax);
                settingSyntax.Setting_ID = settingID;
                section.Ux_Config[componentName] = settingSyntax;
            } else {
                delete section.Ux_Config[componentName];
            }

            sc.patchState({ section: { ...state.section, [sectionID]: section } });
            sc.dispatch(new RefreshObjectRules());
            sc.dispatch(new SetCurrentSection(sectionID));
        }
    }

    @Action(UpdateSectionProperties)
    updateSectionProperties(sc: StateContext<PageStateModel>, action: UpdateSectionProperties) {
        let state = sc.getState();
        let sectionID = action.section.Section_ID;
        let changed = { ...state.section[sectionID] };

        // We need to control what properties can be set at runtime.   Current supported properties:
        //  + Title
        //  + Default_Edit_Layout
        //  + Default_ReadOnly_Layout
        if (action.section && action.section.Title) {
            changed.Title = action.section.Title;
        }
        if (action.section && action.section.Default_Edit_Layout) {
            changed.Default_Edit_Layout = action.section.Default_Edit_Layout;
        }
        if (action.section && action.section.Default_ReadOnly_Layout) {
            changed.Default_ReadOnly_Layout = action.section.Default_ReadOnly_Layout;
        }

        if (!changed.Edit_Status || changed.Edit_Status != 'A') {
            changed.Edit_Status = 'C';
        }

        sc.patchState({ section: { ...state.section, [sectionID]: changed }, dirty: true });
        sc.dispatch(new SetCurrentSection(sectionID));
    }

    @Action(AddPageViews)
    addPageViews(sc: StateContext<PageStateModel>, action: AddPageViews) {
        let state = sc.getState();
        var i: number;
        let changed = {};
        let sectionRuleSets: ObjectRuleSet[] = [];
        let defaultViewID: number = null;

        //sort?

        for (i = 0; i < action.pageViews.length; i++) {
            let viewID = action.pageViews[i].View_ID;
            changed[viewID] = { ...action.pageViews[i] };
            if (!changed[viewID].DynamicSections) {
                changed[viewID].DynamicSections = []; //initialize to empty array if null
            }
            changed[viewID].RuleSets = this.buildUxConfigRuleSets(changed[viewID]);
            if (
                action.defaultPageLayoutCode &&
                action.pageViews[i].Layout_Object_Code &&
                action.pageViews[i].Layout_Object_Code == action.defaultPageLayoutCode
            ) {
                defaultViewID = viewID;
            }

            if (state.designerMode) {
                let ruleSets = changed[viewID].RuleSets;

                // Add an id to each non-core rule if it doesn't already have one.
                // Used for UX Designer dynamic rule editing.
                ruleSets.forEach((ruleSet) => {
                    if (ruleSet.objects) {
                        for (let object in ruleSet.objects) {
                            if (ruleSet.objects[object].fields) {
                                for (let field in ruleSet.objects[object].fields) {
                                    if (ruleSet.objects[object].fields[field].rules) {
                                        for (let rule in ruleSet.objects[object].fields[field].rules) {
                                            let currRule = ruleSet.objects[object].fields[field].rules[rule];
                                            if (currRule.core == false && !currRule.id) {
                                                let newID = this.helper.getNextID(ruleSet.objects[object].fields[field].rules, 'id');
                                                currRule.id = newID;
                                                ruleSet.objects[object].fields[field].rules[rule] = currRule;
                                            }
                                        }
                                    }
                                }
                            }
                        }
                    }
                });
            }
        }

        if (!action.autoAddSections && !state.designerMode) {
            for (var idx in changed) {
                changed[idx].AvailableSections = changed[idx].Sections;
                changed[idx].Sections = [];
            }
        }

        //sc.patchState({ section: { ...state.section, ...changed } })
        sc.patchState({
            pageView: { ...changed },
            currentPageView: null
        }); //now replaces pageviews rather than adding to existing page views   TODO:  append action paramenter not implemented yet
        //if (state.currentPageView == null) {

        if (!defaultViewID) {
            defaultViewID = action.pageViews[0].View_ID;
        }

        sc.dispatch(new SetCurrentPageView(defaultViewID));
        //}
    }

    buildUxConfigRuleSets(pageView: PageView): ObjectRuleSet[] {
        let systemRuleSets: ObjectRuleSet[] = [];
        let sectionRuleSets: ObjectRuleSet[] = [];

        //filter out any existing section-based rule sets.   i.e.  keep only system rule sets
        if (pageView && pageView.RuleSets) {
            systemRuleSets = pageView.RuleSets.filter((rs) => rs.source.toLowerCase().startsWith('config') == false);
        }
        //add section-based rule sets
        //----------------------------------------------------------------------------------------------
        //TODO:  TEMP: BUILD DYNAMIC RULESET FROM SECTIONS.
        //WHEN WE DO THE SDK TRANSACTIONS WE MAY PUT THE LIST OF COMPONENT TEMPLATES IN A NEW PROPERTY
        //ON THE LAYOUT  --- NOTE:  We need to keep the UX desinger mode in mind -- the SDK won't be involved in
        //dynamic rule generation while in the ux desigenr.

        pageView.Sections.forEach((section) => {
            if (section.Ux_Config) {
                ///iterate through components on section and look for rules nodes
                for (var componentName in section.Ux_Config) {
                    if (section.Ux_Config[componentName].RuleSet) {
                        //TODO:  We should add a section GUID and use the GUID instead of the section title below
                        //       Or perhaps use the GUID from the individual component template
                        sectionRuleSets.push({
                            ...section.Ux_Config[componentName].RuleSet,
                            source: 'Config.' + componentName + '.' + section.DB_Section_ID // section.Title.replace(/\s/g, '')
                        });
                    }
                }
            }
        });
        //----------------------------------------------------------------------------------------------

        return [...systemRuleSets, ...sectionRuleSets];
    }

    @Action(SetDirty)
    SetDirty(sc: StateContext<PageStateModel>, action: SetDirty) {
        let state = sc.getState();

        if (state.dirty != true) {
            sc.patchState({ dirty: true });
        }
    }

    @Action(SetPristine)
    SetPristine(sc: StateContext<PageStateModel>, action: SetPristine) {
        let state = sc.getState();

        if (state.dirty != false) {
            sc.patchState({ dirty: false });
        }
    }

    @Action(SetValidEvents)
    SetValidEvents(sc: StateContext<PageStateModel>, action: SetValidEvents) {
        sc.patchState({
            validEvents: action.events,
            saveValidationEvent: action.saveValidationEvent,
            anticipatedValidationEvent: action.anticipatedValidationEvent
        });
    }

    @Action(AddValidEvent)
    AddValidEvent(sc: StateContext<PageStateModel>, action: AddValidEvent) {
        let state = sc.getState();
        let events = cloneDeep(state.validEvents);
        events.push(action.event);
        sc.patchState({
            validEvents: events
        });
    }

    @Action(SetAnticipatedValidationEvent)
    SetAnticipatedValidationEvent(sc: StateContext<PageStateModel>, action: SetAnticipatedValidationEvent) {
        sc.patchState({
            anticipatedValidationEvent: action.anticipatedValidationEvent
        });
    }

    @Action(SetPermittedEvents)
    SetPermittedEvents(sc: StateContext<PageStateModel>, action: SetPermittedEvents) {
        let state = sc.getState();
        sc.patchState({
            permittedEvents: [...action.events]
        });
    }

    @Action(SetMandatoryEvents)
    SetMandatoryEvents(sc: StateContext<PageStateModel>, action: SetMandatoryEvents) {
        let state = sc.getState();
        sc.patchState({
            mandatoryEvents: [...action.events]
        });
    }

    @Action(SetMissingEvents)
    SetMissingEvents(sc: StateContext<PageStateModel>, action: SetMissingEvents) {
        let state = sc.getState();
        sc.patchState({
            missingEvents: [...action.events]
        });
    }

    @Action(ResetState)
    resetState(sc: StateContext<PageStateModel>, action: ResetState) {
        sc.setState({
            pageEvent: 'none',
            singleSectionMode: true,
            currentSection: null,
            selectingSection: null,
            section: null,
            coreSection: null,
            readOnlyMode: false,
            protectedMode: false,
            designerMode: false,
            currentDesignerComponent: null,
            excludedFromSectionReadOnly: null,
            dirty: false,
            pageView: null,
            currentPageView: null,
            validEvents: [],
            navigatorStyle: NavigatorStyleType.Left,
            manualNavigationMode: false,
            objectRuleSets: [],
            objectRuleCompileDirty: true,
            compiledObjectRules: {},
            evaluatedStateObjects: [],
            evaluatedStateEvent: null,
            objectValidationEvents: null,
            saveValidationEvent: null,
            anticipatedValidationEvent: null,
            permittedEvents: [],
            mandatoryEvents: [],
            missingEvents: [],
            savedRules: null,
            pageModel: {},
            trackUnsavedChanges: true,
            lock: {},
            componentSettingList: [],
            userDefinedValidations: [],
            pendingDesignerRuleChange: false
        });
    }

    @Action(ResetObjectRules)
    resetObjectRules(sc: StateContext<PageStateModel>, action: ResetObjectRules) {
        sc.patchState({
            objectRuleSets: [],
            objectRuleCompileDirty: true,
            compiledObjectRules: {},
            evaluatedStateObjects: [],
            evaluatedStateEvent: null
        });
    }

    @Action(RefreshObjectRules)
    refreshObjectRules(sc: StateContext<PageStateModel>, action: RefreshObjectRules) {
        let state = sc.getState();
        let tempPageView = cloneDeep(state.currentPageView); //need to get system rules from current pageview
        tempPageView.Sections = [];
        for (var section in state.section) {
            //add current sections to temp pageview
            tempPageView.Sections.push(state.section[section]);
        }
        let ruleSets: ObjectRuleSet[] = this.buildUxConfigRuleSets(tempPageView);
        sc.dispatch(new AddObjectRuleSets(ruleSets, true));
    }

    @Action(AddObjectRuleSets)
    addObjectRuleSets(sc: StateContext<PageStateModel>, action: AddObjectRuleSets) {
        // TODO: what about keeping track of object sets that have already been added?   add source to ruleSets?
        // TODO: refactor to allow array of RuleSets

        let state = sc.getState();
        let updatedRuleSets = [];

        if (!action.overwrite) {
            updatedRuleSets = [...state.objectRuleSets];
        }

        action.objectRuleSets.forEach((ruleSet) => {
            // 5/12/21 - Made change to not allow the same ruleset to be added twice (keyed by source property)
            let found = false;
            for (let index = 0; index < updatedRuleSets.length; index++) {
                if (updatedRuleSets[index].ruleSet.source == ruleSet.source) {
                    updatedRuleSets[index] = { ruleSet: ruleSet, compiled: false };
                    found = true;
                }
            }
            if (!found) {
                updatedRuleSets.push({ ruleSet: ruleSet, compiled: false });
            }
        });

        //special code to add the rulesets to the current page view ruleset array  -- Used primarily for UD Table base rules
        if (action.addToCurrentPageView) {
            let pageViewRuleSets = [...state.currentPageView.RuleSets];
            action.objectRuleSets.forEach((newRuleSet) => {
                let found = false;
                pageViewRuleSets.forEach((existingRuleSet) => {
                    if (newRuleSet.source == existingRuleSet.source) {
                        existingRuleSet = newRuleSet;
                        found = true;
                    }
                });
                if (!found) {
                    pageViewRuleSets.push(newRuleSet);
                }
            });
            sc.patchState({
                currentPageView: { ...state.currentPageView, RuleSets: pageViewRuleSets }
            });
        }

        sc.patchState({
            pendingDesignerRuleChange: false,
            objectRuleSets: updatedRuleSets,
            objectRuleCompileDirty: true, //force compile of new sets on next use
            compiledObjectRules: action.overwrite ? {} : state.compiledObjectRules,
            evaluatedStateObjects: [],
            evaluatedStateEvent: null
        });
    }

    @Action(SetCompiledRules)
    setCompiledRules(sc: StateContext<PageStateModel>, action: SetCompiledRules) {
        let state = sc.getState();
        let updatedObjectRuleSets = [];
        state.objectRuleSets.forEach((item) => {
            updatedObjectRuleSets.push({ ruleSet: item.ruleSet, compiled: true });
        });
        sc.patchState({
            objectRuleSets: updatedObjectRuleSets,
            compiledObjectRules: action.compiledObjectRules,
            objectRuleCompileDirty: false
        });
    }

    @Action(SetEvaluatedStateObjects)
    setEvaluatedStateObjects(sc: StateContext<PageStateModel>, action: SetEvaluatedStateObjects) {
        let state = sc.getState();
        let anticipatedEvent = state.anticipatedValidationEvent;
        if (action.evaluateStateObjects && action.evaluateStateObjects.length > 0) {
            //if there are error then switch the acticipated event to the event the user selected
            anticipatedEvent = action.event;
        }
        sc.patchState({
            evaluatedStateObjects: action.evaluateStateObjects,
            evaluatedStateEvent: action.event,
            anticipatedValidationEvent: anticipatedEvent
        });
    }

    @Action(SetObjectValidationEvents)
    setObjectValidationEvents(sc: StateContext<PageStateModel>, action: SetObjectValidationEvents) {
        sc.patchState({
            objectValidationEvents: action.objectValidationEvents
        });
    }

    @Action(SaveValidationRules)
    saveValidationRules(sc: StateContext<PageStateModel>, action: SaveValidationRules) {
        const state = sc.getState();
        sc.patchState({
            savedRules: {
                validEvents: cloneDeep(state.validEvents),
                permittedEvents: cloneDeep(state.permittedEvents),
                mandatoryEvents: cloneDeep(state.mandatoryEvents),
                objectRuleSets: cloneDeep(state.objectRuleSets),
                objectRuleCompileDirty: cloneDeep(state.objectRuleCompileDirty),
                compiledObjectRules: cloneDeep(state.compiledObjectRules),
                evaluatedStateObjects: cloneDeep(state.evaluatedStateObjects),
                objectValidationEvents: cloneDeep(state.objectValidationEvents),
                saveValidationEvent: cloneDeep(state.saveValidationEvent),
                anticipatedValidationEvent: cloneDeep(state.anticipatedValidationEvent)
            }
        });
    }

    @Action(RestoreValidationRules)
    restoreValidationRules(sc: StateContext<PageStateModel>, action: RestoreValidationRules) {
        const state = sc.getState();
        if (state && state.savedRules) {
            sc.patchState({
                validEvents: cloneDeep(state.savedRules.validEvents),
                permittedEvents: cloneDeep(state.savedRules.permittedEvents),
                mandatoryEvents: cloneDeep(state.savedRules.mandatoryEvents),
                objectRuleSets: cloneDeep(state.savedRules.objectRuleSets),
                objectRuleCompileDirty: cloneDeep(state.savedRules.objectRuleCompileDirty),
                compiledObjectRules: cloneDeep(state.savedRules.compiledObjectRules),
                evaluatedStateObjects: cloneDeep(state.savedRules.evaluatedStateObjects),
                objectValidationEvents: cloneDeep(state.savedRules.objectValidationEvents),
                saveValidationEvent: cloneDeep(state.savedRules.saveValidationEvent),
                anticipatedValidationEvent: cloneDeep(state.savedRules.anticipatedValidationEvent),
                savedRules: null
            });
        }
    }

    @Action(UpdatePageModel)
    updatePageModel(sc: StateContext<PageStateModel>, action: UpdatePageModel) {
        const state = sc.getState();
        let pageModel = {};
        //if mergeValues then merge new setting with existing page model.  Otherwise overwrite with new settings.
        if (action.mergeValues) {
            pageModel = cloneDeep(state.pageModel);
        }
        pageModel = { ...pageModel, ...action.pageModel };

        sc.patchState({ pageModel: pageModel });
    }

    @Action(ResetPageModel)
    resetPageModel(sc: StateContext<PageStateModel>, action: ResetPageModel) {
        sc.patchState({ pageModel: {} });
    }

    @Action(TrackUnsavedChanges)
    trackUnsavedChanges(sc: StateContext<PageStateModel>, action: TrackUnsavedChanges) {
        sc.patchState({ trackUnsavedChanges: action.on });
    }

    @Action(SetLock)
    setLock(sc: StateContext<PageStateModel>, action: SetLock) {
        sc.patchState({ lock: action.lock });
    }

    @Action(ResetLock)
    resetLock(sc: StateContext<PageStateModel>, action: ResetLock) {
        sc.patchState({ lock: {} });
    }

    @Action(AddToComponentSettingList)
    addToComponentSettingList(sc: StateContext<PageStateModel>, action: AddToComponentSettingList) {
        const state = sc.getState();
        let componentSettingList: ComponentSetting[] = cloneDeep(state.componentSettingList) || [];

        action.componentSettings.forEach((x) => {
            let settingIdx = componentSettingList.findIndex((y) => x.Setting_ID == y.Setting_ID);
            if (settingIdx < 0) {
                componentSettingList.push({
                    Setting_ID: x.Setting_ID,
                    Component_Name: x.Component_Name,
                    Setting_Desc: x.Setting_Desc
                });
            } else {
                if (!action.preserveNames && x.Setting_Desc && x.Setting_Desc > ' ') {
                    componentSettingList[settingIdx].Setting_Desc = x.Setting_Desc;
                }
            }
        });

        sc.patchState({ componentSettingList: componentSettingList });
    }

    @Action(UpdateComponentSetting)
    updateComponentSetting(sc: StateContext<PageStateModel>, action: UpdateComponentSetting) {
        let state = sc.getState();
        let settingID = action.componentSetting.Setting_ID;
        let newSetting = cloneDeep(action.componentSetting);
        let componentSettings: ComponentSetting[] = cloneDeep(state.currentPageView.ComponentSettings) || [];
        let pageView: PageView = { ...state.currentPageView };

        if (settingID == undefined) {
            return;
        }

        // HACK CODE to translate to proper component setting syntax. This action is used to update a normal component 
        // setting structure and also from a Ux_Config structure which
        // has the component settings and the setting id at the root rather than having the settings beneath a Setting_Syntax node.
        // We should consider a refactor to use a standardized structure
        if (!newSetting.Setting_Syntax) {
            newSetting.Setting_Syntax = {};
        }
        if (newSetting.Config) {
            newSetting.Setting_Syntax.Config = { ...newSetting.Config };
            delete newSetting.Config;
        }
        if (newSetting.ChildLayout) {
            newSetting.Setting_Syntax.ChildLayout = { ...newSetting.ChildLayout };
            delete newSetting.ChildLayout;
        }
        if (newSetting.RuleSet) {
            newSetting.Setting_Syntax.RuleSet = { ...newSetting.RuleSet };
            delete newSetting.RuleSet;
        }
        //END HACK

        let foundIndex = componentSettings.findIndex((x) => x.Setting_ID == settingID);
        if (foundIndex < 0) {
            let newEditStatus = settingID.startsWith('-') ? 'A' : 'C';
            if (action.forcedEditStatus) {
                newEditStatus = action.forcedEditStatus;
            }
            componentSettings.push({ ...newSetting, Edit_Status: newEditStatus });
        } else {
            let newEditStatus = componentSettings[foundIndex].Edit_Status;
            if (newEditStatus != 'A') {
                newEditStatus = 'C';
            }
            if (action.forcedEditStatus) {
                newEditStatus = action.forcedEditStatus;
            }
            componentSettings[foundIndex] = { ...componentSettings[foundIndex], ...newSetting, Edit_Status: newEditStatus };
        }
        pageView.ComponentSettings = componentSettings;

        sc.patchState({
            pendingDesignerRuleChange: true,
            pageView: { ...state.pageView, [pageView.View_ID]: pageView },
            currentPageView: pageView
        });
        sc.dispatch(new AddToComponentSettingList([newSetting]));
    }

    @Action(SetUserDefinedValidations)
    setUserDefinedValidations(sc: StateContext<PageStateModel>, action: SetUserDefinedValidations) {
        sc.patchState({
            userDefinedValidations: action.validations
        });
    }
}
